'use client';

// XIcon stays lucide: Solar has no bare X glyph.
import { XIcon } from 'lucide-react';
import { Dialog as DialogPrimitive, Direction } from 'radix-ui';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { shouldIgnoreOutsideInteraction } from '@/lib/outsideInteraction';

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

const DialogTrigger = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger>
>(({ ...props }, ref) => {
  return <DialogPrimitive.Trigger ref={ref} data-slot="dialog-trigger" {...props} />;
});
DialogTrigger.displayName = 'DialogTrigger';

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

const DialogClose = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ ...props }, ref) => {
  return <DialogPrimitive.Close ref={ref} data-slot="dialog-close" {...props} />;
});
DialogClose.displayName = 'DialogClose';

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        className
      )}
      {...props}
    />
  );
});
DialogOverlay.displayName = 'DialogOverlay';

/**
 * Pass as `onOpenAutoFocus` to opt a dialog OUT of the kit's content-focus
 * default and keep Radix's stock first-tabbable autofocus — for surfaces that
 * exist to be typed into and whose input is not reachable by ref (command
 * palettes). Prefer focusing your own ref when you have one.
 */
const keepFirstTabbableAutoFocus = () => undefined;

const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    showCloseButton?: boolean;
    /** Repositions the built-in close (e.g. full-viewport dialogs re-pin it to their inner panel). */
    closeClassName?: string;
  }
>(
  (
    {
      className,
      children,
      showCloseButton = true,
      closeClassName,
      onInteractOutside,
      onOpenAutoFocus,
      dir,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    // Portals render outside the DOM subtree that carries `dir` — read the
    // ambient DirectionProvider (set at the v4 shell root in AppShell).
    const direction = Direction.useDirection(dir as 'ltr' | 'rtl' | undefined);

    const contentRef = React.useRef<HTMLDivElement | null>(null);
    const setContentRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        contentRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );
    // Radix autofocuses the FIRST tabbable on open. When that is a Tooltip
    // trigger the tooltip opens on focus and its dismissable layer — stacked
    // above the dialog's — swallows the first Escape (J25-T03). Land on the
    // content instead (Radix's FocusScope already gives it tabindex="-1");
    // Tab still reaches every control deliberately.
    const focusContentOnOpen = React.useCallback((event: Event) => {
      event.preventDefault();
      contentRef.current?.focus();
    }, []);

    return (
      <DialogPortal data-slot="dialog-portal">
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={setContentRef}
          data-slot="dialog-content"
          onOpenAutoFocus={onOpenAutoFocus ?? focusContentOnOpen}
          dir={direction}
          className={cn(
            // grid-cols-1 (= `repeat(1, minmax(0, 1fr))`): a stock implicit
            // `auto` track sizes to its widest unbreakable item, so one long
            // filename pushed the header and footer outside the max-w-capped
            // panel (CN7-50).
            //
            // rtl:translate-x-1/2 (APL-12708): rtlcss flips `left: 50%`, not
            // Tailwind's translate — unflipped the panel lands off-screen.
            'fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] grid-cols-1 gap-4 rounded-lg border bg-background p-6 text-foreground shadow-lg duration-200 outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 rtl:translate-x-1/2 sm:max-w-lg',
            className
          )}
          onInteractOutside={(event) => {
            onInteractOutside?.(event);
            // Nested-portal guard: a select/popover open inside the dialog must
            // dismiss on outside click WITHOUT taking the dialog down with it.
            if (!event.defaultPrevented && shouldIgnoreOutsideInteraction(event.target)) {
              event.preventDefault();
            }
          }}
          {...props}
        >
          {children}
          {showCloseButton && (
            <DialogPrimitive.Close
              data-slot="dialog-close"
              className={cn(
                "absolute end-4 top-4 cursor-pointer rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                closeClassName
              )}
            >
              <XIcon aria-hidden="true" />
              <span className="sr-only">{t('CommonText.close')}</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = 'DialogContent';

const DialogHeader = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-slot="dialog-header"
        className={cn('flex flex-col gap-2 text-center sm:text-start', className)}
        {...props}
      />
    );
  }
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<'div'> & {
    showCloseButton?: boolean;
  }
>(({ className, showCloseButton = false, children, ...props }, ref) => {
  const { t } = useTranslation();

  return (
    <div
      ref={ref}
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">{t('CommonText.close')}</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  );
});
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Title
      ref={ref}
      data-slot="dialog-title"
      className={cn('text-lg leading-none font-semibold', className)}
      {...props}
    />
  );
});
DialogTitle.displayName = 'DialogTitle';

const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Description
      ref={ref}
      data-slot="dialog-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
});
DialogDescription.displayName = 'DialogDescription';

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  keepFirstTabbableAutoFocus,
};
